import { Helmet } from "react-helmet-async";
import { COMPANY } from "@/data/site";

const SITE_URL = "https://compubrain.ai";

export const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "CompuBrain AI",
  legalName: "CompuBrain AI",
  url: SITE_URL,
  logo: COMPANY.logo,
  description:
    "CompuBrain AI is India's leading Generative Engine Optimization (GEO) consulting and implementation company, helping businesses get discovered, trusted and recommended by AI search engines.",
  foundingDate: "2005",
  parentOrganization: { "@type": "Organization", name: "CompuBrain" },
  address: {
    "@type": "PostalAddress",
    streetAddress: "CompuBrain House, Anjali Society #8, Maninagar",
    addressLocality: "Ahmedabad",
    addressRegion: "Gujarat",
    postalCode: "380008",
    addressCountry: "IN",
  },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: COMPANY.phone,
    contactType: "sales",
    email: COMPANY.salesEmail,
    areaServed: "Worldwide",
  },
  sameAs: [
    COMPANY.social.linkedin,
    COMPANY.social.twitter,
    COMPANY.social.instagram,
    COMPANY.social.facebook,
  ],
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: "CompuBrain AI",
  publisher: { "@id": `${SITE_URL}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: { "@type": "EntryPoint", urlTemplate: `${SITE_URL}/blog?q={search_term_string}` },
    "query-input": "required name=search_term_string",
  },
};

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export default function Seo({
  title,
  description,
  path = "/",
  image = COMPANY.logo,
  schemas = [],
  type = "website",
}) {
  const fullTitle = title ? `${title} | CompuBrain AI` : "CompuBrain AI — Generative Engine Optimization (GEO) Consulting";
  const url = `${SITE_URL}${path}`;
  const allSchemas = [orgSchema, websiteSchema, ...schemas];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="CompuBrain AI" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@compubrain" />

      {allSchemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
